import React, { useCallback, useContext, useEffect, useState } from "react";
import { withRouter } from "react-router-dom";
import { injectIntl, InjectedIntlProps } from "react-intl";
import { RouteComponentProps } from "react-router";
import { classNamesFunction } from "@fluentui/react/lib/Utilities";
import { Stack, IStackStyles } from "@fluentui/react/lib/Stack";
import { ApplicationContext, ServiceContext } from "@msx/platform-services";
import { messages } from "./Signin.messages";
import { getStyles } from "./Signin.styles";
import { BusyIndicator } from "../../../core/components";
import { PrimaryButton } from "@fluentui/react/lib/Button";

const getClassNames = classNamesFunction<any, any>();
let classes: any;

interface OwnProps extends InjectedIntlProps {
  isUserLoggedIn: any;
}

type Props = OwnProps & InjectedIntlProps & RouteComponentProps;

const SigninComponent: React.FC<Props> = (props) => {
  const { intl } = props;
  const { appState } = useContext(ApplicationContext);
  const { authClient } = useContext(ServiceContext);
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(props.isUserLoggedIn);

  // When the portal is embedded (e.g. inside the Dynamics model-driven app
  // web resource), MSAL cannot silently acquire a token and the browser blocks
  // the fallback popup unless it originates from a user gesture. In that case
  // we surface an explicit "Sign in" button instead of auto-firing the popup.
  const isInIframe = window.parent !== window;

  classes = getClassNames(getStyles, appState.theme);

  useEffect(() => {
    //MOUNT
    // Outside an iframe MSAL uses the redirect flow, which needs no user
    // gesture, so start it automatically. Inside an iframe we wait for the
    // explicit sign-in click below to satisfy the browser's popup gesture rule.
    if (!isInIframe && !isUserLoggedIn) {
      handleGetStartedButonClick();
      setIsUserLoggedIn(true);
    }

    //UNMOUNT
    return () => { };
    // eslint-disable-next-line
  }, []);

  const handleGetStartedButonClick = useCallback(() => {
    authClient.acquireToken(["user.read", "user.readbasic.all"]);
  }, [authClient]);

  const handleSignInClick = useCallback(async () => {
    try {
      // A user gesture is present here, so the MSAL popup is allowed to open.
      await authClient.login(true);
      // Reload so the shell re-runs startup and picks up the cached account.
      window.location.reload();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Interactive sign-in failed", e);
    }
  }, [authClient]);

  const renderMain = () => {
    const stackTokens = { childrenGap: 20 };
    const stackStyles: Partial<IStackStyles> = {
      root: { minHeight: "calc(80vh)" },
    };
    const containerStyles: React.CSSProperties = { minHeight: "100%" };

    // Embedded (iframe) and not yet signed in: show an explicit sign-in button.
    if (isInIframe && !isUserLoggedIn) {
      return (
        <div style={containerStyles}>
          <Stack
            horizontalAlign="center"
            verticalAlign="center"
            tokens={stackTokens}
            styles={stackStyles}
          >
            <span className={classes.signInHint}>
              {intl.formatMessage(messages.firstTimeSignInHint)}
            </span>
            <PrimaryButton
              text={intl.formatMessage(messages.getStartedButtonText)}
              onClick={handleSignInClick}
            />
          </Stack>
        </div>
      );
    }

    return (
      <div>{isUserLoggedIn ? (<div style={containerStyles}>
        <Stack
          horizontalAlign="center"
          verticalAlign="center"
          tokens={stackTokens}
          styles={stackStyles}
        >
          <BusyIndicator message={intl.formatMessage(messages.waitText)} />
        </Stack>
      </div>) : (<div>Logging in...</div>)}</div>
    )
  }

  return renderMain();
};

export const Signin = withRouter(injectIntl(SigninComponent));

// Generated by GitHub Copilot | author: Raviteja Madhira | Updated on: 2026-07-21 | 3 lines added | Added first-time "Click here to proceed" hint above the embedded Sign in button (renders only when in-iframe and not yet signed in, i.e. before token handoff)

// Generated by GitHub Copilot | author: Raviteja Madhira | Updated on: 2026-07-13 | ~40 lines modified | Embedded (iframe) portal: show explicit user-gesture Sign in button instead of auto-firing the browser-blocked MSAL popup; reload after interactive login
